// NumberInput.tsx
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { InputStyles } from './InputStyles';
import type { AppTheme } from '@/theme/index';

interface NumberInputProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  isEdit?: boolean;
  disabled?: boolean;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  ranges?: [number, number][];
  error?: string;
  hint?: string; // 외부에서 전달받은 힌트
  isFocused?: boolean;
  onFocusChange?: (focused: boolean) => void;
  id?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  decimalPoint?: number; // 소수점 자리수 (0 이상의 정수)
}

const Input = styled.input<{
  disabled?: boolean;
  hasError?: boolean;
  theme?: AppTheme;
  $size?: 'xs' | 'sm' | 'md' | 'lg';
}>`
  width: 100%;
  height: 26px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError ? theme.colors.border.feedback.error : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;

  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  text-align: right;
  padding-right: ${({ theme }) => theme.spacing[14]}px;

  &:focus {
    border: 1px solid
      ${({ hasError, theme }) =>
        hasError ? theme.colors.border.feedback.error : theme.colors.border.indicator};
    box-shadow: 0 0 0 2px
      ${({ hasError, theme }) =>
        hasError
          ? `${theme.colors.status.danger.stroke.border}33`
          : `${theme.colors.border.indicator}33`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  background: ${({ theme }) => theme.colors.surface.feedback.error};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm}px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 10;
  line-height: 1.2;

  /* 말풍선 꼬리 (삼각형) */
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 8px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${({ theme }) => theme.colors.surface.feedback.error};
  }
`;

const HintMessage = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  background: ${({ theme }) => theme.colors.surface.feedback.helper};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm}px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 10;
  line-height: 1.2;

  /* 말풍선 꼬리 (삼각형) */
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 8px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${({ theme }) => theme.colors.surface.feedback.helper};
  }
`;

const UnitLabel = styled.span`
  position: absolute;
  right: ${({ theme }) => theme.spacing[2]}px;
  top: 50%;
  transform: translateY(-50%);

  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-style: normal;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  line-height: 1;
  letter-spacing: 0%;
  color: ${({ theme }) => theme.colors.text.secondary};
  pointer-events: none;
  display: flex;
  align-items: center;
`;

export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  isEdit = true,
  disabled = false,
  unit,

  step,
  error,
  hint,
  isFocused: externalIsFocused,
  onFocusChange,
  id,
  name,
  size = 'sm',
  decimalPoint,
}) => {
  // decimal point에 맞게 값을 포맷팅하는 함수
  const formatValue = (val: number, dp?: number): string => {
    if (typeof val !== 'number' || isNaN(val)) {
      return '';
    }
    // decimalPoint가 지정되어 있으면 포맷팅 적용
    if (dp !== undefined && dp >= 0) {
      return val.toFixed(dp);
    }
    return val.toString();
  };

  const [inputValue, setInputValue] = useState<string>(
    typeof value === 'number' && isNaN(value) ? '' : formatValue(value, decimalPoint)
  );
  const [internalIsFocused, setInternalIsFocused] = useState<boolean>(false);
  const isDisabled = disabled || !isEdit;

  // 외부에서 제어하는 경우 외부 상태 사용, 아니면 내부 상태 사용
  const isFocused = externalIsFocused !== undefined ? externalIsFocused : internalIsFocused;

  useEffect(() => {
    // 포커스가 있을 때는 입력 중이므로 inputValue를 변경하지 않음
    if (isFocused) {
      return;
    }

    // NaN이면 빈 문자열로, 그렇지 않으면 decimal point에 맞게 포맷팅
    if (typeof value === 'number' && isNaN(value)) {
      setInputValue('');
    } else {
      setInputValue(formatValue(value, decimalPoint));
    }
  }, [value, decimalPoint, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // step이 1이면 정수만 허용, 그렇지 않으면 소수점도 허용
    const regex = step === 1 ? /[^\d-]/g : /[^\d.-]/g;
    const validInput = inputValue.replace(regex, '');

    // step이 1이면 소수점 입력 방지
    let filteredInput = validInput;
    if (step === 1) {
      // 정수만 허용하므로 소수점 제거
      filteredInput = validInput.replace(/\./g, '');
    } else {
      // 소숫점이 여러 개 입력되는 것을 방지
      const parts = validInput.split('.');
      filteredInput = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : validInput;
    }

    // 마이너스 기호는 맨 처음에만 허용
    let finalInput = filteredInput;
    if (filteredInput.includes('-')) {
      // 마이너스가 맨 처음이 아니면 제거
      if (filteredInput.indexOf('-') !== 0) {
        finalInput = filteredInput.replace(/-/g, '');
      } else {
        // 맨 처음에만 마이너스 허용, 나머지는 제거
        finalInput = '-' + filteredInput.substring(1).replace(/-/g, '');
      }
    }

    setInputValue(finalInput);

    // 실시간으로 onChange 호출
    const num = step === 1 ? parseInt(finalInput, 10) : parseFloat(finalInput);
    if (!isNaN(num)) {
      onChange(num);
    } else if (finalInput === '' || finalInput === '-') {
      // 빈 값이거나 마이너스 기호만 있는 경우는 undefined를 전달
      onChange(undefined as unknown as number);
    }
  };

  // 고유 ID 생성
  const inputId = id || (label ? `input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  return (
    <InputStyles.InputContainer>
      <InputStyles.StyledInputRow>
        {label && <InputStyles.Label htmlFor={inputId}>{label}</InputStyles.Label>}

        <InputStyles.InputWrapper style={{ position: 'relative' }}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {isFocused && hint && !error && <HintMessage>{hint}</HintMessage>}
          <Input
            id={inputId}
            name={name || inputId}
            type="text"
            inputMode="decimal"
            disabled={isDisabled}
            value={inputValue}
            onChange={handleChange}
            onFocus={() => {
              const newFocused = true;
              if (externalIsFocused !== undefined) {
                onFocusChange?.(newFocused);
              } else {
                setInternalIsFocused(newFocused);
              }
            }}
            onBlur={() => {
              const newFocused = false;
              if (externalIsFocused !== undefined) {
                onFocusChange?.(newFocused);
              } else {
                setInternalIsFocused(newFocused);
              }

              // decimal point가 지정되어 있으면 값 반올림 및 포맷팅
              if (decimalPoint !== undefined && decimalPoint >= 0) {
                const num = step === 1 ? parseInt(inputValue, 10) : parseFloat(inputValue);
                if (!isNaN(num)) {
                  const multiplier = Math.pow(10, decimalPoint);
                  const rounded = Math.round(num * multiplier) / multiplier;
                  setInputValue(rounded.toFixed(decimalPoint));
                  onChange(rounded);
                }
              } else {
                // decimal point가 없으면 그대로 포맷팅만
                const num = step === 1 ? parseInt(inputValue, 10) : parseFloat(inputValue);
                if (!isNaN(num)) {
                  setInputValue(num.toString());
                }
              }
            }}
            hasError={!!error}
            $size={size}
          />
          {unit && <UnitLabel>{unit}</UnitLabel>}
        </InputStyles.InputWrapper>
      </InputStyles.StyledInputRow>
    </InputStyles.InputContainer>
  );
};
